import React, {useEffect, useState} from 'react';

import {InputReact} from 'common/components/input';
import {InputSize} from 'common/components/input/types';
import {useTranslation} from 'common/i18n/hooks';
import {ButtonReact} from 'common/components/button';
import {ButtonSize, ButtonTheme} from 'common/components/button/constants';
import {promptFeedbackModal} from 'common/widgets/feedback-modal';
import {FeedbackForFeature, Sentiment} from 'common/widgets/feedback-modal/types';

import styles from './prompter.module.styl';

export function Prompter(props: {loading: boolean; responseText: string; onSubmit: (prompt: string) => void}) {
  const {t} = useTranslation();

  const [value, setValue] = useState(``);

  useEffect(() => {
    if (!props.loading) {
      setValue(``);
    }
  }, [props.loading]);

  return (
    <>
      <Feedback />
      <div className={styles.wrapper}>
        <div className={styles.container}>
          <InputReact
            className={styles.input}
            size={InputSize.Medium}
            icon={props.loading ? `spinner` : `spark`}
            value={value}
            disabled={props.loading}
            placeholder={t(`query-builder.buckets.prompter-placeholder`)}
            autoFocus
            onInput={(ev) => setValue(ev.currentTarget.value)}
            onKeydown={(ev) => {
              if (ev.key === `Enter`) {
                ev.stopPropagation();
                props.onSubmit(value);
              }
            }}
          />
          {!!props.responseText && <p className={styles.response}>{props.responseText}</p>}
        </div>
      </div>
    </>
  );
}

function Feedback() {
  const [submitted, setSubmitted] = useState(false);

  const onClick = async (sentiment: Sentiment) => {
    const [success] = await promptFeedbackModal(
      {
        feature: FeedbackForFeature.TextAnalysis,
        'negative-label': ``,
        placeholder: `Additional feedback (Optional)`,
        'sentiment-heading': `Rate your satisfaction with the Text Analysis feature`,
        'positive-label': ``,
        title: `Additional Text Analysis Feedback`,
      },
      {
        extraFeedbackEventName: `[AI] Text Analysis Feedback`,
        initialSentiment: sentiment,
      },
    );

    if (success) {
      setSubmitted(true);
    }
  };

  if (submitted) {
    return null;
  }

  return (
    <div className={styles.feedbackContainer}>
      <div className={styles.feedbackLeft}>
        <span className={styles.feedbackAlpha}>Alpha</span>
        <span className={styles.feedbackText}>Help us improve Text Analysis by providing feedback.</span>
      </div>
      <div className={styles.feedbackButtons}>
        <ButtonReact
          className={styles.feedbackButton}
          icon='thumbs-up'
          size={ButtonSize.Tiny}
          theme={ButtonTheme.Tertiary}
          tooltip={{
            text: `I am satisfied`,
          }}
          onClick={() => onClick(Sentiment.Positive)}
        />
        <ButtonReact
          className={styles.feedbackButton}
          icon='thumbs-down'
          size={ButtonSize.Tiny}
          theme={ButtonTheme.Tertiary}
          tooltip={{
            text: `I am not satisfied`,
          }}
          onClick={() => onClick(Sentiment.Negative)}
        />
      </div>
    </div>
  );
}
